import type { LibraryTreeItemType } from './getLibrariesFolderTree';
import type { LibraryAsset } from '~/elc/features/library/api';
import type { Library } from '~/elc/features/library';
import type { ItemFoldersAPIContentTypes } from '~/elc/features/admin-common';
import type { CMSAssetItem } from '../../types';
import {
  Col,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Row,
  Button,
  Tree,
  findNodesInTree,
  useToast,
} from '@workramp/ui';
import { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { I18n } from '~/common/explicit-exports';
import { hideIntercomButton, showIntercomButton } from '~/utils';
import { MixPanel } from '~/common';

import { useAddLibraryContent } from '~/elc/features/library/api';
import { CMSAdminMixpanelEvents, CMS_ASSET_DETAILS_KEY } from '../../constants';
import { useGetLibraries } from '../../api/getLibraries';
import { useGetLibrariesFolderTree } from './getLibrariesFolderTree';
import { InvalidKeys } from './constants';

type CMSLibrariesDrawerProps = {
  asset: CMSAssetItem;
  isOpen: boolean;
  onClose: () => void;
};
const { APP_CONSTANTS } = window;

export const CMSLibrariesDrawer = ({ asset, isOpen, onClose }: CMSLibrariesDrawerProps) => {
  const [selectedLibraryId, setSelectedLibraryId] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());
  const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set());
  const { open: openToast } = useToast();
  const addLibraryContent = useAddLibraryContent(selectedLibraryId);
  const libariesQuery = useGetLibraries();
  const queryClient = useQueryClient();

  const ADD_TO_LIBRARY_LABEL = I18n.t('js.admin.cms.libraries.title');
  const CANCEL_TEXT = I18n.t('js.admin.cms.actions.cancel');
  const ADD_TEXT = I18n.t('js.admin.cms.actions.add');

  const treeData = useGetLibrariesFolderTree({ libraryId: selectedLibraryId, expandedKeys });
  const mapFirstLevel = useMemo(() => new Set(treeData.map((item) => item.id)), [treeData]);

  const isDisabled = useMemo(() => {
    const noKeysSelected = selectedKeys.size === 0;
    const assetIsAlreadyInLocation = asset.locations.some((location) => {
      const nodes = findNodesInTree(selectedKeys, treeData) as LibraryTreeItemType[];
      const { libraryId, folderId } = nodes[0] || {};
      const isAlreadyInFolder = location.libraryFolderId === selectedKeys.values().next().value;
      const isAlreadyInLibrary = location.libraryId === libraryId && !folderId && !location.libraryFolderId;
      return isAlreadyInFolder || isAlreadyInLibrary;
    });
    return noKeysSelected || assetIsAlreadyInLocation;
  }, [selectedKeys, asset?.locations, treeData]);

  useEffect(() => {
    if (libariesQuery.isSuccess && libariesQuery.data?.length === 1) {
      const libraryId = libariesQuery.data[0].id;
      setSelectedLibraryId(libraryId);
      setExpandedKeys(new Set([libraryId]));
    }
  }, [libariesQuery.isSuccess, libariesQuery.data]);

  const librariesMap = useMemo(() => {
    const map = new Map<string, Library>();
    libariesQuery.data
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .forEach((library) => {
        map.set(library.id, library);
      });
    return map;
  }, [libariesQuery.data]);

  const handleOnSave = () => {
    const nodes = findNodesInTree(selectedKeys, treeData) as LibraryTreeItemType[];

    // in the case of a library the id is the library id and the libraryId is not defined
    const { libraryId, folderId } = nodes[0];

    const isFirstLevelLibrarySelected = !folderId;

    const libraryAssets: LibraryAsset[] = [
      {
        id: asset.topLevelContentId,
        type:
          asset.assetType === APP_CONSTANTS.CMS_ASSET_TYPES.FILE
            ? ('cms_file' as ItemFoldersAPIContentTypes)
            : ('cms_link' as ItemFoldersAPIContentTypes),
      },
    ];
    addLibraryContent.mutate(
      {
        libraryId,
        data: {
          university_folder_id: isFirstLevelLibrarySelected ? null : folderId,
          assets: libraryAssets,
        },
      },
      {
        onSuccess: async () => {
          const keyToInvalidate = [CMS_ASSET_DETAILS_KEY, asset.assetType?.toUpperCase(), asset.id];
          await queryClient.invalidateQueries(keyToInvalidate);
          MixPanel.track(CMSAdminMixpanelEvents.ASSET_ADDED_TO_LIB_FROM_LIB, { asset_type: asset.assetType });
          handleOnClose();

          if (isFirstLevelLibrarySelected) {
            openToast({
              children: I18n.t('js.admin.cms.libraries.content_add_success_message', {
                title: asset.name,
                libraryName: librariesMap.get(libraryId).name,
              }),
            });
          } else {
            openToast({
              children: I18n.t('js.admin.cms.libraries.content_add_to_folder_success_message', {
                title: asset.name,
                libraryName: librariesMap.get(libraryId).name,
                folderName: nodes[0].name,
              }),
            });
          }
        },
        onError: () => {
          openToast({
            children: I18n.t('js.admin.cms.libraries.content_add_failure_message', {
              assetType: I18n.t(`js.admin.cms.${asset.assetType.toLowerCase()}`),
            }),
          });
        },
      },
    );
  };

  const handleOnClose = () => {
    setExpandedKeys(new Set());
    setSelectedKeys(new Set());
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleOnClose} onEnter={hideIntercomButton} onExited={showIntercomButton}>
      <DrawerHeader title={ADD_TO_LIBRARY_LABEL} data-testid="add-to-library-drawer-title" />
      <DrawerBody>
        <Tree
          data={treeData}
          expandedKeys={expandedKeys}
          selectedKeys={selectedKeys}
          disabledKeys={new Set([InvalidKeys.loadingKey, InvalidKeys.errorKey])}
          onExpandedChange={(keys) => {
            setExpandedKeys(keys);
          }}
          onSelectionChange={(keys) => {
            if (mapFirstLevel.has(Array.from(keys)[0] as string)) {
              setSelectedLibraryId(Array.from(keys)[0] as string);
            }
            setSelectedKeys(keys);
          }}
        />
      </DrawerBody>
      <DrawerFooter
        actions={
          <Row gapX={2}>
            <Col xs="auto">
              <Button variant="text" color="secondary" onPress={handleOnClose}>
                {CANCEL_TEXT}
              </Button>
            </Col>
            <Col xs="auto">
              <Button onPress={handleOnSave} isDisabled={isDisabled}>
                {ADD_TEXT}
              </Button>
            </Col>
          </Row>
        }
      />
    </Drawer>
  );
};
